import { useState, useEffect, useCallback, useRef, useMemo, Component, ErrorInfo, ReactNode, CSSProperties } from "react";
import { diffWords } from "diff";

// Browser globals not in TS lib. Declared as `any` because they are
// optional/3rd-party and we use them defensively (capability checks).
declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
    pdfjsLib?: any;
  }
}

import type { RequestRow, ContractRow, UserRow, LawyerRow, ContractType } from "./types/api";

import { T } from "./theme";
import {
  ALL_STATUSES,
  STATUS_COLORS,
  REQUEST_TYPES,
  PRIORITIES,
  VERSION_LABELS,
  DEPARTMENTS,
  AI_EXTRACT_FIELDS,
  AI_UNIVERSAL_FIELDS,
} from "./constants";
import { uid, today, daysAgo, fmtDate, toTitleCase, capitalize, mapStatusFromDB, mapStatusToDB } from "./utils";
import { renderMarkdown } from "./components/markdown";
import {
  Badge, ConfBadge, VoiceInput, ChatExportButtons,
  Btn, RefreshBtn, Inp, Sel, Modal, Card,
  SectionTitle, TabBar, SearchBar, EmptyState,
} from "./components/primitives";
import { IntakeModal } from "./components/IntakeModal";
import { apiCall } from "./apiClient";
import { RequestDetailModal } from "./components/RequestDetail";
import { DashboardView, ExecutiveDashboard, RequestorDashboard } from "./components/Dashboards";
import { InHouseDashboard } from "./components/InHouseDashboard";
import { MyDeskView } from "./components/MyDeskView";
import { RequestsView, QueueView, GlobalSearchView } from "./components/RequestsViews";
import { RepoView, ContractFormModal, ChildrenModal, RelationshipGraphModal } from "./components/Repository";
import { PdfHighlightModal } from "./components/PdfHighlightModal";
import { AIImportView, ValidationView, ExtractionDashView, RedlineReview, DraftingView, SecurityView } from "./components/AiViews";
import { CrossRepoChatView, SelfServiceView, SelfServiceTemplateManager, SalesforceView, ExtractionProfileManager, PlatformHealthTab, ParentRequirementsManager, AdminView, ReportBuilder, MetricsView, CustomDashboardView, ReportsView, LawyerWorkbench, ExecutionQueueView, NotificationsView, SharedContractView, ResponsePage } from "./components/Views";
import { LoginScreen } from "./components/LoginScreen";
import { PlatformConsole } from "./components/PlatformConsole";
import { SetupWizard } from "./components/SetupWizard";
import { ForceChangePasswordScreen } from "./components/ForceChangePasswordScreen";
import { PublicGuide } from "./components/PublicGuide";
import { AcceptInvitePage } from "./components/AcceptInvitePage";
import { HelpView } from "./components/HelpView";
import { Sidebar } from "./components/Sidebar";
import { ClientScopeProvider, useClientScope } from "./contexts/ClientScopeContext";
import { LAWYERS } from "./sampleData";
import { SF_INSTANCE, SF_OPPORTUNITIES, SF_STAGE_COLORS, sfLink } from "./salesforce";


/* ═══════════════════ ERROR BOUNDARY ═══════════════════ */
interface ErrorBoundaryProps { children: ReactNode }
interface ErrorBoundaryState { hasError: boolean; error: Error | null }
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error: Error): ErrorBoundaryState { return { hasError: true, error }; }
  componentDidCatch(error: Error, info: ErrorInfo) { console.error("React Error Boundary:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: "center", backgroundColor: "#F5F7FA", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⚠</div>
            <div style={{ fontSize: 16, color: "#D04040", marginBottom: 8, fontWeight: 700 }}>Something went wrong</div>
            <div style={{ fontSize: 12, color: "#5A6278", marginBottom: 20, maxWidth: 400 }}>{this.state.error?.message || "An unexpected error occurred"}</div>
            <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }} style={{ padding: "10px 24px", borderRadius: 6, border: "none", backgroundColor: "#2B7A3E", color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Reload Application</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}


/* ═══════════════════ MAIN ═══════════════════ */
function KontractAIInner() {
  const { edition: scopeEdition } = useClientScope();
  const [user, setUser] = useState<UserRow | null>(() => {
    try { const _u = localStorage.getItem("kontractai_user"); return _u ? JSON.parse(_u) : null; } catch { return null; }
  });
  const [token, setTokenState] = useState<string | null>(() => localStorage.getItem("kontractai_token"));
  const [view, setView] = useState("dashboard");
  const [draftingTargetRequestId, setDraftingTargetRequestId] = useState<any>(null);
  const [draftingActiveJobId, setDraftingActiveJobId] = useState<any>(null);
  const [draftingSelectedRequestId, setDraftingSelectedRequestId] = useState<any>(null);
  const [importKey, setImportKey] = useState(0);
  const [requests, setRequests] = useState<any[]>([]);
  const [repo, setRepo] = useState<any[]>([]);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [lawyers, setLawyers] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [contractTypes, setContractTypes] = useState<any[]>([]);
  const [dashboardStats, setDashboardStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [sharedToken, setSharedToken] = useState<any>(null);
  const [pendingNavContract, setPendingNavContract] = useState<any>(null);
  const [pendingNavRequest, setPendingNavRequest] = useState<any>(null);
  // Deep-link state: set when a notification click or /requests/{id}?tab=... URL
  // is loaded, consumed once by RequestDetailModal on first render for that request.
  const [pendingNavRequestTab, setPendingNavRequestTab] = useState<string | null>(null);
  const [pendingNavRequestThreadId, setPendingNavRequestThreadId] = useState<string | null>(null);
  const [pendingNavRequestVersionId, setPendingNavRequestVersionId] = useState<string | null>(null);
  const [pendingNavRequestEnvelopeId, setPendingNavRequestEnvelopeId] = useState<string | null>(null);
  const [pendingSearchQuery, setPendingSearchQuery] = useState("");
  const [pendingRepoFilters, setPendingRepoFilters] = useState<any>(null);
  const [pendingRequestFilters, setPendingRequestFilters] = useState<any>(null);
  const [sharedContract, setSharedContract] = useState<any>(null);
  const [sharedError, setSharedError] = useState<any>(null);
  const [helpDeepLinkSlug, setHelpDeepLinkSlug] = useState<string | null>(null);

  // Detect URL parameters that deep-link into the SPA:
  //   /requests/{uuid}[?tab=...&thread=...&version=...&envelope=...] — notification deep links
  //   ?repoContract={id} — duplicate-detection modal View button
  //   ?openRequest={id} — duplicate-detection modal View button
  // After consuming the URL we rewrite the history entry to "/" so a refresh
  // does not re-trigger navigation and the browser bar stays clean.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathname = window.location.pathname;

    // /requests/{uuid} deep link (notifications, email CTAs)
    const requestMatch = pathname.match(/^\/requests\/([0-9a-f-]{36})\/?$/i);
    if (requestMatch) {
      const requestIdFromPath = requestMatch[1];
      setPendingNavRequest(requestIdFromPath);
      setPendingNavRequestTab(params.get('tab') || null);
      setPendingNavRequestThreadId(params.get('thread') || null);
      setPendingNavRequestVersionId(params.get('version') || null);
      setPendingNavRequestEnvelopeId(params.get('envelope') || null);
      setView('requests');
      window.history.replaceState({}, '', '/');
      return;
    }

    const repoContract = params.get('repoContract');
    const openRequest = params.get('openRequest');
    if (repoContract) {
      setPendingNavContract(repoContract);
      setView('repo');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (openRequest) {
      setPendingNavRequest(openRequest);
      setView('requests');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Detect /shared/ URL
  useEffect(() => {
    const path = window.location.pathname;
    if (path.startsWith('/shared/')) {
      const tok = path.split('/shared/')[1];
      if (tok) {
        setSharedToken(tok);
        fetch(`/api/share/view/${tok}`)
          .then(r => { if (!r.ok) throw new Error('This link is no longer valid or has expired.'); return r.json(); })
          .then(data => setSharedContract(data))
          .catch(err => setSharedError(err.message));
      }
    }
    // Handle DocuSign OAuth callback hash
    if (window.location.hash === '#docusign-connected' || window.location.hash === '#docusign-sent') {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const handleLogin = useCallback((userData: any, authToken: string) => {
    setUser(userData);
    setTokenState(authToken);
  }, []);

  // /help and /help/:slug deep link — sets view='help' once the user is authed.
  // PublicGuide handles the /guide path before this component hits the auth check.
  useEffect(() => {
    const m = window.location.pathname.match(/^\/help(?:\/([^/]+))?\/?$/);
    if (m && user) {
      setHelpDeepLinkSlug(m[1] || null);
      setView('help');
      window.history.replaceState({}, '', '/');
    }
  }, [user]);

  // Handle SSO token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');
    const ssoError = params.get('sso_error');
    if (ssoToken) {
      localStorage.setItem('kontractai_token', ssoToken);
      fetch('/api/auth/me', { headers: { Authorization: `Bearer ${ssoToken}` } })
        .then(r => r.json())
        .then(data => {
          if (data.user) {
            const normalized = {
              ...data.user,
              must_change_password: data.user.must_change_password ?? data.user.mustChangePassword ?? false,
            };
            localStorage.setItem('kontractai_user', JSON.stringify(normalized));
            setUser(normalized);
            setTokenState(ssoToken);
          }
        }).catch(() => {});
      window.history.replaceState({}, '', '/');
    }
    if (ssoError) {
      alert('SSO Error: ' + ssoError);
      window.history.replaceState({}, '', '/');
    }
  }, []);

  // Deep-link handler for ?requestId=<uuid> — used by handoff emails and any
  // other external link into a specific request. Only fires once the user is
  // authenticated; the query param is stripped after navigation so a refresh
  // does not re-trigger. UUID regex guards against noise values.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const requestId = params.get('requestId');
    if (!requestId) return;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(requestId)) {
      setPendingNavRequest(requestId);
      setView('requests');
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('requestId');
    window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
  }, [user]);

  const handleLogout = useCallback(() => {
    setUser(null);
    setTokenState(null);
    localStorage.removeItem("kontractai_token");
    localStorage.removeItem("kontractai_user");
    setView("dashboard");
  }, []);

  // Silent token refresh — refresh JWT every 6 days to avoid expiration during active use
  useEffect(() => {
    if (!user) return;
    // Refresh on load if token is older than 6 days
    try {
      const t = localStorage.getItem('kontractai_token');
      if (t) {
        const payload = JSON.parse(atob(t.split('.')[1]));
        const age = Date.now() - (payload.iat * 1000);
        if (age > 6 * 24 * 60 * 60 * 1000) {
          fetch('/api/auth/refresh', { method: 'POST', headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } })
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.token) { localStorage.setItem('kontractai_token', d.token); setTokenState(d.token); console.log('[Auth] Token refreshed on load'); } })
            .catch(() => {});
        }
      }
    } catch (e: any) { /* ignore decode errors */ }
    // Periodic refresh every 6 days
    const interval = setInterval(async () => {
      try {
        const t = localStorage.getItem('kontractai_token');
        if (!t) return;
        const res = await fetch('/api/auth/refresh', { method: 'POST', headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } });
        if (res.ok) { const d = await res.json(); if (d.token) { localStorage.setItem('kontractai_token', d.token); setTokenState(d.token); console.log('[Auth] Token refreshed silently'); } }
      } catch (err: any) { console.error('[Auth] Silent refresh failed:', err); }
    }, 6 * 24 * 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  // Redirect business_reviewer to requests view
  // Data filter, not permission gate.
  useEffect(() => {
    if (user?.role === 'business_reviewer' && view === 'dashboard') setView('requests');
  }, [user, view]);

  // ═══════════════════ GRANULAR DATA FETCHING ═══════════════════
  // Each data domain has its own fetcher with queuing so updates are never dropped.
  // If an event arrives while a fetch is in-flight, a re-fetch is queued automatically.
  const initialLoadDone = useRef(false);

  // Fetch-with-queue: each fetcher has its own active/queued state via refs.
  // If called while already running, queues exactly one re-fetch for when it finishes.
  // The ref-based approach ensures the queue state persists across re-renders.
  const fetcherState = useRef<Record<string, any>>({
    requests: { active: false, queued: false },
    contracts: { active: false, queued: false },
    notifications: { active: false, queued: false },
    stats: { active: false, queued: false },
    static: { active: false, queued: false },
  });

  const runFetcher = useCallback(async (key: string, fn: any) => {
    const state = fetcherState.current[key];
    if (state.active) { state.queued = true; return; }
    state.active = true;
    try { await fn(); } catch (e: any) { console.error(`[Fetch:${key}] error:`, e); }
    state.active = false;
    if (state.queued) { state.queued = false; runFetcher(key, fn); }
  }, []);

  const mapRequestFromDB = useCallback((r: any): any => ({
    id: r.id, title: r.title, type: r.request_type, requester: r.requester_name || r.business_owner || "Unknown",
    requesterEmail: r.requester_email || "", dept: r.department, business_unit: r.business_unit, counterparty: r.counterparty,
    counterpartyContact: r.counterparty_contact, status: mapStatusFromDB(r.status),
    agreementCategory: r.agreement_category, businessOwner: r.business_owner || "",
    assignedTo: r.assigned_to, priority: capitalize(r.priority), targetDate: r.target_date,
    created: r.created_at, updated: r.updated_at,
    description: r.description, attachments: [], versions: [], activity: [],
    sfOppId: r.sf_opportunity_id, sfOppName: r.sf_opportunity_name, sfAmount: r.sf_amount,
    assignedName: r.assigned_name, assignedEmail: r.assigned_email,
    routing_rule: r.routing_rule,
    // Counterparty validation fields (2026-04-08)
    counterpartyAddress: r.counterparty_address || "",
    counterpartyEntityType: r.counterparty_entity_type || "",
    counterpartyJurisdiction: r.counterparty_jurisdiction || "",
    // SLA tracking (Item 6, in-house edition only surface)
    slaTargetDate: r.sla_target_date || null,
    slaSeverity: r.sla_severity || null,
    // Display number (Display Number lane, 2026-04-30). Preserved as
    // snake_case so the formatDisplayNumber helper finds them on the row.
    display_number: r.display_number ?? null,
    client_display_code: r.client_display_code ?? null,
    // Cancel-and-supersede (Display Number Session 4, 2026-04-30).
    superseded_by_id: r.superseded_by_id ?? null,
    cancellation_reason: r.cancellation_reason ?? null,
    // Naming and Folder Structure lane (2026-04-30).
    parent_request_id: r.parent_request_id ?? null,
    parent_contract_id: r.parent_contract_id ?? null,
    parent_is_online_terms: r.parent_is_online_terms ?? false,
    online_terms_url: r.online_terms_url ?? null,
    online_terms_party: r.online_terms_party ?? null,
    governance_type: r.governance_type ?? null,
    amendment_number: r.amendment_number ?? null,
    child_sequence_number: r.child_sequence_number ?? null,
  }), []);

  const mapContractFromDB = useCallback((c: any) => {
    if (!c || typeof c !== "object") return null;
    return {
      id: c.id,
      name: c.name || `${c.counterparty_entity || "Unknown"} - ${c.contract_type || "Agreement"}`,
      type: c.contract_type || "Agreement",
      company: c.company_entity || "",
      counterparty: c.counterparty_entity || "",
      effectiveDate: c.effective_date || null,
      terminationDate: c.termination_date || null,
      terminationDateVerbatim: c.termination_date_verbatim || null,
      term: c.term || null,
      termVerbatim: c.term_verbatim || null,
      executionDate: c.execution_date || null,
      governingLaw: c.governing_law || "",
      autoRenewal: c.auto_renewal || false,
      renewalTerm: c.renewal_term || "",
      noticePeriod: c.notice_period || "",
      contractOwner: c.contract_owner_id || "",
      businessOwner: c.business_owner || "",
      dept: c.department || "",
      value: c.contract_value ? `$${Number(c.contract_value).toLocaleString()}` : null,
      file: c.file_name || "",
      filePath: c.file_path || "",
      sfOppId: c.sf_opportunity_id || null,
      sfOppName: c.sf_opportunity_name || null,
      clientId: c.client_id || null,
      clientName: c.client_name || "",
      agreementCategory: c.agreement_category || "",
      sharepointUrl: c.sharepoint_url || null,
      aiExtracted: c.ai_extracted || false,
      // Counterparty validation fields (2026-04-08)
      counterpartyAddress: c.counterparty_address || "",
      counterpartyEntityType: c.counterparty_entity_type || "",
      counterpartyJurisdiction: c.counterparty_jurisdiction || "",
      status: c.status || "active",
      // Display number (Display Number lane, 2026-04-30). Preserved as
      // snake_case so the formatDisplayNumber helper finds them on the row.
      display_number: c.display_number ?? null,
      client_display_code: c.client_display_code ?? null,
      // Cancel-and-supersede (Display Number Session 4, 2026-04-30).
      superseded_by_id: c.superseded_by_id ?? null,
      cancellation_reason: c.cancellation_reason ?? null,
      // Naming and Folder Structure lane (2026-04-30).
      parent_contract_id: c.parent_contract_id ?? null,
      governance_type: c.governance_type ?? null,
      online_terms_party: c.online_terms_party ?? null,
      amendment_number: c.amendment_number ?? null,
      child_sequence_number: c.child_sequence_number ?? null,
      children: Array.isArray(c.children) ? c.children.map(mapContractFromDB).filter(Boolean) : [],
      activity: Array.isArray(c.activity) ? c.activity.map((a: any) => ({ date: a.created_at?.split("T")[0] || a.date, action: a.action, by: a.performer_name || a.by || "System" })) : [],
    };
  }, []);

  // Individual domain fetchers — each uses runFetcher with its own queue key
  const fetchRequests = useCallback(() => runFetcher('requests', async () => {
    if (!token) return;
    const data = await apiCall("/requests");
    if (data?.requests) setRequests(data.requests.map(mapRequestFromDB));
  }), [token, runFetcher, mapRequestFromDB]);

  const fetchContracts = useCallback(() => runFetcher('contracts', async () => {
    if (!token) return;
    const data = await apiCall("/contracts");
    if (data?.contracts) {
      const source = data.contracts?.length > 0 ? data.contracts : data.flat || [];
      setRepo(source.map(mapContractFromDB).filter(Boolean));
    }
  }), [token, runFetcher, mapContractFromDB]);

  const fetchNotifications = useCallback(() => runFetcher('notifications', async () => {
    if (!token) return;
    const data = await apiCall("/notifications");
    if (data?.notifications) setNotifications(data.notifications.map((n: any) => ({ id: n.id, type: n.type, msg: n.message, date: n.created_at?.split("T")[0], read: n.is_read, link: n.link || null, context: n.context || null, entityType: n.entity_type || null, entityId: n.entity_id || null })));
  }), [token, runFetcher]);

  const fetchDashboardStats = useCallback(() => runFetcher('stats', async () => {
    if (!token) return;
    const data = await apiCall("/dashboard/stats");
    if (data) setDashboardStats(data);
  }), [token, runFetcher]);

  const fetchStaticData = useCallback(() => runFetcher('static', async () => {
    if (!token) return;
    const [lawyerData, clientData, typesData] = await Promise.all([
      apiCall("/users/lawyers"),
      apiCall("/clients"),
      apiCall("/contract-types"),
    ]);
    if (lawyerData?.lawyers) setLawyers(lawyerData.lawyers.map((l: LawyerRow) => ({ id: l.id, name: l.name, email: l.email, specialty: l.lawyer_specialty || "" })));
    if (clientData?.clients) setClients(clientData.clients);
    if (typesData?.types) setContractTypes(typesData.types);
  }), [token, runFetcher]);

  // Full fetch — used for initial load and manual refresh after user actions
  const fetchData = useCallback(async () => {
    if (!token) return;
    if (!initialLoadDone.current) setLoading(true);
    await Promise.all([
      fetchRequests(),
      fetchContracts(),
      fetchNotifications(),
      fetchDashboardStats(),
      fetchStaticData(),
    ]);
    setLoading(false);
    initialLoadDone.current = true;
  }, [token, fetchRequests, fetchContracts, fetchNotifications, fetchDashboardStats, fetchStaticData]);

  // Initial data load
  useEffect(() => { fetchData(); }, [fetchData]);

  // ═══════════════════ SSE REAL-TIME UPDATES ═══════════════════
  // Granular: each event type only refreshes the relevant data domains.
  // Debounce per-domain to coalesce rapid-fire events (e.g. batch imports).
  useEffect(() => {
    if (!token) return;
    let es: any;
    let reconnectTimer: any;
    let reconnectAttempts = 0;
    const debounceTimers: Record<string, any> = {};

    const debouncedCall = (key: string, fn: any, delay = 400) => {
      clearTimeout(debounceTimers[key]);
      debounceTimers[key] = setTimeout(fn, delay);
    };

    const onRequestChange = () => {
      debouncedCall('requests', fetchRequests);
      debouncedCall('stats', fetchDashboardStats);
      debouncedCall('notifs', fetchNotifications);
    };
    const onContractChange = () => {
      debouncedCall('contracts', fetchContracts);
      debouncedCall('stats', fetchDashboardStats);
      debouncedCall('notifs', fetchNotifications);
    };

    const connect = () => {
      es = new EventSource(`/api/events?token=${encodeURIComponent(token)}`);
      es.addEventListener('connected', () => {
        console.log('[SSE] Connected for real-time updates');
        reconnectAttempts = 0;
      });
      es.addEventListener('request_created', onRequestChange);
      es.addEventListener('request_updated', onRequestChange);
      es.addEventListener('contract_created', onContractChange);
      es.addEventListener('contract_updated', onContractChange);
      es.onerror = () => {
        es.close();
        // Exponential backoff: 2s, 4s, 8s, 16s, max 30s
        reconnectAttempts++;
        const delay = Math.min(2000 * Math.pow(2, reconnectAttempts - 1), 30000);
        console.log(`[SSE] Disconnected, reconnecting in ${delay / 1000}s (attempt ${reconnectAttempts})`);
        reconnectTimer = setTimeout(connect, delay);
      };
    };
    connect();

    // Background polling fallback every 30s — catches anything SSE might miss
    const bgRefresh = setInterval(() => {
      fetchRequests();
      fetchContracts();
      fetchNotifications();
      fetchDashboardStats();
    }, 30000);

    return () => {
      if (es) es.close();
      clearTimeout(reconnectTimer);
      Object.values(debounceTimers).forEach(clearTimeout);
      clearInterval(bgRefresh);
    };
  }, [token, fetchRequests, fetchContracts, fetchNotifications, fetchDashboardStats]);

  // Manual refresh function for after user actions (full refresh)
  const refreshData = useCallback(() => { setTimeout(fetchData, 300); }, [fetchData]);

  // Refresh data when navigating to repo view (picks up newly imported contracts)
  useEffect(() => { if (view === "repo") fetchContracts(); }, [view]);

  // Force AIImportView remount when navigating to import (ensures batch resume check)
  useEffect(() => { if (view === "import") setImportKey(k => k + 1); }, [view]);

  const handleUpdate = useCallback(async (id: string, upd: any) => {
    // If _deleted, remove from list and refresh
    if (upd._deleted) {
      setRequests(prev => prev.filter((r: any) => r.id !== id));
      setTimeout(() => { fetchRequests(); fetchDashboardStats(); }, 300);
      return;
    }
    // If _refreshOnly, status was already changed server-side (e.g. by draft upload)
    if (upd._refreshOnly) {
      const { _refreshOnly, ...patch } = upd;
      setRequests(prev => prev.map((r: any) => r.id === id ? { ...r, ...patch, updated: today() } : r));
      setTimeout(() => { fetchRequests(); fetchDashboardStats(); }, 300);
      return;
    }

    // Map frontend status names to DB format
    const apiUpd: Record<string, any> = {};
    if (upd.status) apiUpd.status = mapStatusToDB(upd.status);
    if (upd.statusReason) apiUpd.status_reason = upd.statusReason;
    if (upd.assignedTo !== undefined) apiUpd.assigned_to = upd.assignedTo;
    if (upd.priority) apiUpd.priority = upd.priority.toLowerCase();

    const result = await apiCall(`/requests/${id}`, { method: "PUT", body: JSON.stringify(apiUpd) });
    if (result) {
      // Optimistic update + refetch
      setRequests(prev => prev.map((r: any) => r.id === id ? { ...r, ...upd, updated: today() } : r));
      setTimeout(() => { fetchRequests(); fetchDashboardStats(); fetchNotifications(); }, 300);
    } else {
      // Fallback to local update
      setRequests(prev => prev.map((r: any) => r.id === id ? { ...r, ...upd, updated: today() } : r));
    }
  }, [fetchRequests, fetchDashboardStats, fetchNotifications]);

  const handleAdd = useCallback(async (form: any) => {
    // Spread the form and rename only the fields whose API name differs from the
    // form-state name. This pattern (per OPERATIONS_AND_CONVENTIONS.md §2.5.4)
    // means new form fields automatically reach the backend without having to
    // remember to add them to a hardcoded list. Backend ignores unknown keys.
    const {
      // frontend-only / not for the API
      attachmentFiles, attachments, dept,
      // camelCase aliases — re-emitted below as snake_case
      type, counterpartyContact, businessOwner, targetDate,
      sfOppId, sfOppName, sfAmount, sfContractTerms, sfPaymentTerms,
      agreementCategory, priority,
      counterpartyAddress, counterpartyEntityType, counterpartyJurisdiction,
      ...rest
    } = form;
    const apiData = {
      ...rest,
      request_type: type,
      agreement_type: type || null,
      counterparty_contact: counterpartyContact,
      business_owner: businessOwner,
      priority: priority?.toLowerCase(),
      target_date: targetDate,
      sf_opportunity_id: sfOppId,
      sf_opportunity_name: sfOppName,
      sf_amount: sfAmount,
      sf_contract_terms: sfContractTerms,
      sf_payment_terms: sfPaymentTerms,
      agreement_category: agreementCategory || null,
      counterparty_address: counterpartyAddress || undefined,
      counterparty_entity_type: counterpartyEntityType || undefined,
      counterparty_jurisdiction: counterpartyJurisdiction || undefined,
    };

    const result = await apiCall("/requests", { method: "POST", body: JSON.stringify(apiData) });
    if (result) {
      // Upload attachment files if any
      if (form.attachmentFiles && form.attachmentFiles.length > 0 && result.request?.id) {
        const token = localStorage.getItem("kontractai_token");
        for (const file of form.attachmentFiles) {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("label", "current_draft");
          formData.append("notes", "Uploaded with contract request");
          try {
            await fetch(`/api/versions/request/${result.request.id}`, {
              method: "POST",
              headers: { "Authorization": `Bearer ${token}` },
              body: formData
            });
          } catch (err: any) { console.error("Attachment upload failed:", err); }
        }
      }
      setTimeout(() => { fetchRequests(); fetchDashboardStats(); }, 300);
    } else {
      // Fallback local add
      const nr = { id: uid(), title: form.title, type: form.type, requester: user!.name, requesterEmail: user!.email, dept: form.dept, counterparty: form.counterparty, counterpartyContact: form.counterpartyContact, status: "Pending Assignment", assignedTo: null as any, priority: form.priority, targetDate: form.targetDate, created: today(), updated: today(), description: form.description, attachments: form.attachments, sfOppId: form.sfOppId || null, sfOppName: form.sfOppName || null, sfAmount: form.sfAmount || null, versions: [] as any[], activity: [{ date: today(), action: "Request submitted", by: user!.name }] };
      setRequests(prev => [nr, ...prev]);
    }
  }, [user, fetchRequests, fetchDashboardStats]);

  const handleMarkRead = useCallback(async (id: string) => {
    if (id === "all") {
      await apiCall("/notifications/read-all", { method: "PUT" });
      setNotifications(p => p.map(n => ({ ...n, read: true })));
    } else {
      await apiCall(`/notifications/${id}/read`, { method: "PUT" });
      setNotifications(p => p.map(n => n.id === id ? { ...n, read: true } : n));
    }
  }, []);

  // Notification click: mark read AND navigate to the deep-link target.
  // Parses `link` (produced by the backend notification builder) to figure
  // out the target view and request-level deep-link params. Falls back to
  // /requests/{entityId} when link is absent but entity_type === 'request'.
  const handleNotificationActivate = useCallback(async (n: any) => {
    if (!n) return;
    if (!n.read) { void handleMarkRead(n.id); }
    const href: string = n.link || (n.entityType === 'request' && n.entityId ? `/requests/${n.entityId}` : '');
    if (!href) return;
    // Accept absolute or relative
    let pathname = href;
    let search = '';
    try {
      const u = new URL(href, window.location.origin);
      pathname = u.pathname;
      search = u.search;
    } catch { /* treat as relative */ }
    const params = new URLSearchParams(search);
    const m = pathname.match(/^\/requests\/([0-9a-f-]{36})\/?$/i);
    if (m) {
      setPendingNavRequest(m[1]);
      setPendingNavRequestTab(params.get('tab') || null);
      setPendingNavRequestThreadId(params.get('thread') || null);
      setPendingNavRequestVersionId(params.get('version') || null);
      setPendingNavRequestEnvelopeId(params.get('envelope') || null);
      setView('requests');
    }
  }, [handleMarkRead]);

  const unread = notifications.filter((n: any) => !n.read).length;

  // Render response page (public, no auth needed).
  // Case-insensitive — defensive against any caller who generates uppercase
  // hex tokens (production uses crypto.randomBytes toString('hex') which is
  // lowercase, but a mixed-case token should still route here, not dashboard).
  const respondMatch = window.location.pathname.match(/^\/respond\/([a-f0-9]{64})$/i);
  if (respondMatch) return <ResponsePage token={respondMatch[1]} />;

  // Render shared contract view (public, no auth needed)
  if (sharedToken) return <SharedContractView data={sharedContract} error={sharedError} token={sharedToken} />;

  // Public route: /accept-invite/:token (Wave 2.5 hotfix)
  const acceptInviteMatch = window.location.pathname.match(/^\/accept-invite\/([^/]+)\/?$/);
  if (acceptInviteMatch) {
    return <AcceptInvitePage token={acceptInviteMatch[1]} />;
  }

  // Public guide (no auth needed). Matches /guide and /guide/:slug.
  const guideMatch = window.location.pathname.match(/^\/guide(?:\/([^/]+))?\/?$/);
  if (guideMatch) {
    return <PublicGuide initialSlug={guideMatch[1] || null} />;
  }

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  const pwChangeRequired = !!(user && token && ((user as any).must_change_password || (user as any).mustChangePassword));
  if (pwChangeRequired && token) {
    return (
      <ForceChangePasswordScreen
        user={user as any}
        token={token}
        capturedPassword={null}
        onSuccess={({ user: updatedUser }) => {
          setUser(updatedUser);
        }}
        onLogout={() => {
          localStorage.removeItem("kontractai_token");
          localStorage.removeItem("kontractai_user");
          setUser(null);
          setTokenState(null);
        }}
      />
    );
  }

  const renderView = () => {
    switch (view) {
      case "dashboard": {
          const dashOnNav = (target: any, options: any) => {
            if (target === 'repo') { if (options?.filters) setPendingRepoFilters(options.filters); setView('repo'); }
            else if (target === 'requests') { if (options?.selectId) setPendingNavRequest(options.selectId); if (options?.filters) setPendingRequestFilters(options.filters); setView('requests'); }
            else if (target === 'search') { setPendingSearchQuery(options?.query || ''); setView('search'); }
          };
          const dashOnSelect = (r: any) => { setPendingNavRequest(r.id); setView('requests'); };
          // Dashboard variant selection (edition-gated) — not a permission gate.
          if (scopeEdition === 'in_house' && user && ['admin', 'lawyer', 'legalops', 'contract_manager'].includes(user.role || '')) return <InHouseDashboard />;
          // RBAC Phase 1: dashboard component is chosen by roles.dashboard_variant
          // (delivered in auth /me + /login responses). Migration 072
          // backfilled every user.role_id + role.dashboard_variant, so the
          // legacy per-role fallback is gone.
          const variant = user?.dashboard_variant || 'DashboardView';
          if (variant === 'LawyerWorkbench') return <LawyerWorkbench requests={requests} user={user} onSelect={dashOnSelect} onNav={dashOnNav} />;
          if (variant === 'RequestorDashboard') return <RequestorDashboard requests={requests} user={user} onSelect={dashOnSelect} />;
          if (variant === 'ExecutiveDashboard') return <ExecutiveDashboard requests={requests} repo={repo} dashboardStats={dashboardStats} lawyers={lawyers} onNav={dashOnNav} />;
          return <DashboardView requests={requests} user={user} repo={repo} dashboardStats={dashboardStats} lawyers={lawyers} onNav={dashOnNav} />;
        }
      case "requests": return <RequestsView requests={requests} user={user} onUpdate={handleUpdate} onAdd={handleAdd} onRefresh={fetchRequests} pendingSelect={pendingNavRequest} onClearPending={() => setPendingNavRequest(null)} pendingSelectTab={pendingNavRequestTab} pendingSelectThreadId={pendingNavRequestThreadId} pendingSelectVersionId={pendingNavRequestVersionId} pendingSelectEnvelopeId={pendingNavRequestEnvelopeId} onClearPendingNav={() => { setPendingNavRequestTab(null); setPendingNavRequestThreadId(null); setPendingNavRequestVersionId(null); setPendingNavRequestEnvelopeId(null); }} pendingFilters={pendingRequestFilters} onClearFilters={() => setPendingRequestFilters(null)} contractTypes={contractTypes} onNavigateToDrafting={(requestId: string) => { if (requestId) setDraftingTargetRequestId(requestId); setView("drafting"); }} onNav={(type: string, id: string) => { if (type === 'repo') { setPendingNavContract(id); setView('repo'); } else if (type === 'request') { setPendingNavRequest(id); } }} />;
      case "queue": return <QueueView requests={requests} user={user} onUpdate={handleUpdate} onNavigateToDrafting={(requestId: string) => { if (requestId) setDraftingTargetRequestId(requestId); setView("drafting"); }} />;
      case "mydesk": return <MyDeskView onOpen={(id: string) => { setPendingNavRequest(id); setView('requests'); }} />;
      case "repo": return <RepoView repo={repo} user={user} onRefresh={refreshData} clients={clients} pendingSelect={pendingNavContract} onClearPending={() => setPendingNavContract(null)} pendingFilters={pendingRepoFilters} onClearFilters={() => setPendingRepoFilters(null)} contractTypes={contractTypes} onNavigateToRequest={(id: string) => { setPendingNavRequest(id); setView('requests'); }} onAdd={handleAdd} />;
      case "import": return <AIImportView key={importKey} onRefresh={refreshData} clients={clients} />;
      case "validation": return <ValidationView />;
      case "extractiondash": return <ExtractionDashView />;
      case "search": return <GlobalSearchView requests={requests} repo={repo} onSelectContract={(id: string) => { setPendingNavContract(id); setView('repo'); }} onSelectRequest={(id: string) => { setPendingNavRequest(id); setView('requests'); }} pendingQuery={pendingSearchQuery} onClearPending={() => setPendingSearchQuery("")} />;
      case "drafting": return <DraftingView key="drafting" user={user} targetRequestId={draftingTargetRequestId} onTargetConsumed={() => setDraftingTargetRequestId(null)} persistedJobId={draftingActiveJobId} onJobChange={setDraftingActiveJobId} persistedRequestId={draftingSelectedRequestId} onRequestSelect={setDraftingSelectedRequestId} />;
      case "aichat": return <CrossRepoChatView />;
      case "selfservice": return <SelfServiceView user={user} />;
      case "execqueue": return <ExecutionQueueView onNavigate={setView} />;
      case "reports": return <ReportsView user={user} />;
      case "salesforce": return <SalesforceView requests={requests} repo={repo} onAdd={handleAdd} user={user} />;
      case "security": return <SecurityView />;
      case "notifications": return <NotificationsView notifications={notifications} onMarkRead={handleMarkRead} onActivate={handleNotificationActivate} />;
      case "help": return <HelpView initialSlug={helpDeepLinkSlug} />;
      case "admin": return <AdminView currentUser={user} requests={requests} repo={repo} onAdd={handleAdd} onRefresh={refreshData} />;
      // Operator-only console. Render-gated on is_operator (server-side
      // /api/platform gate is authoritative); non-operators fall through to
      // the dashboard rather than seeing the console.
      case "platform": return user?.is_operator ? <PlatformConsole /> : <DashboardView requests={requests} user={user} repo={repo} />;
      case "wizard": return user?.is_operator ? <SetupWizard /> : <DashboardView requests={requests} user={user} repo={repo} />;
      default: return <DashboardView requests={requests} user={user} repo={repo} />;
    }
  };

  return (
    <ClientScopeProvider token={token}>
    <ScopeRefetchTrigger onChange={fetchData} />
    <div style={{ display: "flex", minHeight: "100vh", backgroundColor: T.bg, fontFamily: "'Outfit', 'Segoe UI', sans-serif", color: T.tx, fontSize: 16 }}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&family=Source+Serif+4:wght@400;600;700;800&display=swap" rel="stylesheet" />
      <style>{`html { zoom: 1.0; } body { margin: 0; }`}</style>
      <Sidebar view={view} onNav={setView} user={user} onLogout={handleLogout} notifCount={unread} />
      <main style={{ flex: 1, padding: "32px 40px", overflow: "auto", maxHeight: "100vh" }}>
        {loading && requests.length === 0 ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 24, marginBottom: 8, opacity: .5 }}>⟳</div>
              <div style={{ fontSize: 13, color: T.txM }}>Loading...</div>
            </div>
          </div>
        ) : (() => { try { return renderView(); } catch (err: any) { console.error("Render error:", err); return <div style={{ padding: 40, textAlign: "center" }}><div style={{ fontSize: 28, marginBottom: 8 }}>⚠</div><div style={{ fontSize: 14, color: T.red, marginBottom: 8 }}>Something went wrong rendering this view.</div><div style={{ fontSize: 12, color: T.txM, marginBottom: 16 }}>{err?.message || "Unknown error"}</div><Btn v="primary" onClick={() => { setView("dashboard"); window.location.reload(); }}>Return to Dashboard</Btn></div>; } })()}
      </main>
    </div>
    </ClientScopeProvider>
  );
}

// Lives inside <ClientScopeProvider>; fires the parent's refetch when the
// picker selection changes after the initial mount. The first effect run is
// skipped because KontractAIInner already triggers the same fetchData via its
// own mount useEffect — without the guard we would double-fetch on load.
function ScopeRefetchTrigger({ onChange }: { onChange: () => void }) {
  const { selected } = useClientScope();
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    onChange();
  }, [selected, onChange]);
  return null;
}

export default function KontractAI() {
  return <ErrorBoundary><KontractAIInner /></ErrorBoundary>;
}
